import React, { Fragment, useEffect, useMemo } from 'react';

import {
    Anchor,
    Box,
    Button,
    Heading,
    IconCheckmarkCircle,
    IconInfoOutline,
    IconMail,
    IconPhone,
    Image,
    List,
    Paragraph,
    Popover,
    Popper,
    RatingStars,
    Text,
    TriggerButton,
} from '@zillow/constellation';
import type { HomeRecsCarouselProps } from '@zillow/home-recs-carousels';
import { RecsCarousel } from '@zillow/home-recs-carousels';
import { event } from '@zillow/universal-analytics';
import { yieldCallback } from '@zillow/yield-callback';

import type { BridgeTeamPAAgent } from '../../fixtures/bridgeContainer';

const BACKGROUND_PHOTO_WIDTH_DESKTOP = 374;

const BulletIcon: React.FC = () => (
    <Box
        // Typescript not allowing Icon in the `as` prop
        as={IconCheckmarkCircle as unknown as undefined}
        // @ts-expect-error - size is a prop on Icon
        size={24}
        fontColor="gray500"
        sx={{
            '&&': {
                verticalAlign: 'middle',
                marginRight: '$spacing.sm',
                marginBottom: 'calc($spacing.xs / 2)',
            },
        }}
    />
);

const AgentContactRow: React.FC<{
    agent: BridgeTeamPAAgent;
    userFirstName: null | string | undefined;
}> = ({ agent, userFirstName }) => {
    const agentFirstName = agent.name?.split(' ')[0] ?? '';
    const ratingAverage = agent.profile?.ratingAverage ?? 0;

    return (
        <Box
            sx={{
                'display': 'grid',
                'gap': '$spacing.md',
                'gridTemplateAreas': `
                    "header"
                    "card"
                    "list"
                `,
                '@media $lg': {
                    gridTemplateAreas: `
                        "header card"
                        "list   card"
                    `,
                    gap: '$spacing.lg',
                },
                // Contains the absolutely position photo
                'position': 'relative',
            }}
        >
            <Box
                sx={{
                    gridArea: 'header',
                }}
                as={Heading}
                fontType="h4"
                level="3"
            >
                {userFirstName ? `${userFirstName}, your` : 'Your'} agent is{' '}
                <Box as="span" sx={{ color: '$colors.red500' }}>
                    ready to help
                </Box>
            </Box>

            <Box
                sx={{
                    gridArea: 'list',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '$spacing.lg',
                }}
            >
                <Box
                    as={List}
                    appearance="none"
                    sx={{
                        'display': 'none',
                        '@media $lg': {
                            'display': 'flex',
                            'flexDirection': 'column',
                            'gap': '$spacing.sm',
                            '& > li': {
                                padding: 0,
                            },
                        },
                    }}
                >
                    <List.Item>
                        <BulletIcon /> Discover new homes in your search area
                    </List.Item>
                    <List.Item>
                        <BulletIcon /> Schedule tours for homes you love
                    </List.Item>
                    <List.Item>
                        <BulletIcon /> Answer questions about homes you find
                    </List.Item>
                </Box>

                <Box
                    sx={{
                        'display': 'flex',
                        'gap': '$spacing.sm',
                        'flexDirection': 'column',
                        '@media $lg': {
                            flexDirection: 'row',
                        },
                    }}
                >
                    <Button
                        buttonType="primary"
                        icon={<IconMail />}
                        as="a"
                        onClick={yieldCallback(() => {
                            event({
                                // https://dataportal.zillowgroup.net/event-registry/6092
                                envelope: {
                                    event_type_id: '6092',
                                    event_type_version_id: '2',
                                    event_template_id: '171',
                                    event_template_version_id: '1',
                                    // Master ID: 6092.2.171.1
                                },
                                clickstream_trigger: {
                                    trigger_type_nm: 'interaction',
                                    trigger_location_nm: 'home',
                                    trigger_source_nm: 'my_agent_component',
                                    trigger_object_nm: 'my_agent_component|email',
                                },
                                semantic: {
                                    semantic_event_nm: 'my_agent_homepage_click_email',
                                },
                            });
                        })}
                        href={`mailto:${String(agent.email)}`}
                    >
                        Email {agentFirstName}
                    </Button>
                    <Button
                        buttonType="primary"
                        icon={<IconPhone />}
                        as="a"
                        onClick={yieldCallback(() => {
                            event({
                                // https://dataportal.zillowgroup.net/event-registry/6093
                                envelope: {
                                    event_type_id: '6093',
                                    event_type_version_id: '2',
                                    event_template_id: '171',
                                    event_template_version_id: '1',
                                    // Master ID: 6093.2.171.1
                                },
                                clickstream_trigger: {
                                    trigger_type_nm: 'interaction',
                                    trigger_location_nm: 'home',
                                    trigger_source_nm: 'my_agent_component',
                                    trigger_object_nm: 'my_agent_component|phone',
                                },
                                semantic: {
                                    semantic_event_nm: 'my_agent_homepage_click_phone',
                                },
                            });
                        })}
                        href={`tel:${String(agent.phone).replace(/-/g, '')}`}
                    >
                        <Box as="span" sx={{ '@media $lg': { display: 'none' } }}>
                            Call&nbsp;
                        </Box>
                        {agent.phone}
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    gridArea: 'card',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                {/* Agent Card */}
                <Box
                    sx={{
                        'display': 'flex',
                        'flexDirection': 'row',
                        'gap': '$spacing.xs',
                        'boxShadow': '$shadows.shadow300',
                        'padding': '$spacing.sm',
                        'borderRadius': '$radii.lg',
                        'background': '$colors.white',
                        '@media $xl': {
                            padding: '$spacing.md',
                            gap: '$spacing.sm',
                            marginRight: 0.65 * BACKGROUND_PHOTO_WIDTH_DESKTOP,
                        },
                    }}
                >
                    <Box
                        sx={{
                            'borderRadius': '$radii.pill',
                            'flexShrink': 0,
                            'overflow': 'hidden',
                            'width': 64,
                            'height': 64,
                            '@media $xl': {
                                width: 96,
                                height: 96,
                            },
                        }}
                    >
                        {agent.photoUrl && (
                            <Image
                                src={agent.photoUrl}
                                width="100%"
                                height="100%"
                                alt=""
                                role="presentation"
                            />
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box
                            sx={{
                                'display': 'flex',
                                'flexDirection': 'column',
                                'alignItems': 'flex-start',
                                'gap': '$spacing.xxs',
                                '@media $xl': {
                                    gap: '$spacing.xs',
                                },
                            }}
                        >
                            <Box
                                as={Paragraph}
                                sx={{
                                    borderRadius: '$radii.pill',
                                    backgroundColor: '$colors.orangeRed100',
                                    whiteSpace: 'nowrap',
                                    padding: '$spacing.xxs $spacing.xs',
                                }}
                                fontType="finePrintHeading"
                            >
                                Your agent
                            </Box>
                            <Text fontType="bodyHeading" fontColor="brand">
                                <Anchor
                                    href={`${
                                        // This should always be non-null but
                                        // graphql currently says it can be.
                                        agent.profile?.profileUrl ?? ''
                                    }`}
                                    onClick={yieldCallback(() => {
                                        event({
                                            // https://dataportal.zillowgroup.net/event-registry/6094
                                            envelope: {
                                                event_type_id: '6094',
                                                event_type_version_id: '2',
                                                event_template_id: '171',
                                                event_template_version_id: '1',
                                                // Master ID: 6094.2.171.1
                                            },
                                            clickstream_trigger: {
                                                trigger_type_nm: 'interaction',
                                                trigger_location_nm: 'home',
                                                trigger_source_nm: 'my_agent_component',
                                                trigger_object_nm: 'my_agent_component|name',
                                            },
                                            semantic: {
                                                semantic_event_nm: 'my_agent_homepage_click_name',
                                            },
                                        });
                                    })}
                                >
                                    {agent.name}
                                </Anchor>
                            </Text>
                        </Box>

                        {agent.hasPremierAgent && (
                            <Text>
                                Zillow Premier Agent{' '}
                                <Popper
                                    triggered={
                                        <Popover
                                            renderBody={
                                                <Paragraph>
                                                    Zillow Premier Agent is an advertising program
                                                    that helps connect customers to local real
                                                    estate professionals. Zillow Premier Agent
                                                    partners are paid advertisers and are not
                                                    affiliated with Zillow, Inc. brokerage or any of
                                                    its affiliates.
                                                </Paragraph>
                                            }
                                        />
                                    }
                                >
                                    <Box
                                        as={TriggerButton}
                                        sx={{ '&&': { verticalAlign: 'middle' } }}
                                    >
                                        <IconInfoOutline fontColor="brand" size="xs" />
                                    </Box>
                                </Popper>
                            </Text>
                        )}

                        <Text>{agent.businessName}</Text>

                        <Text>
                            {agent.profile?.officeCity}, {agent.profile?.officeState}
                        </Text>

                        <Box
                            sx={{
                                'display': 'flex',
                                'flexDirection': 'column',
                                '@media $md': {
                                    flexDirection: 'row',
                                },
                            }}
                        >
                            <Box>
                                <Box
                                    as={RatingStars}
                                    sx={{ marginRight: '$spacing.xs' }}
                                    value={ratingAverage}
                                    aria-label={`${ratingAverage} out of 5 stars`}
                                ></Box>
                                <Text>{ratingAverage}</Text>
                            </Box>
                            <Box
                                as={Text}
                                sx={{
                                    'display': 'none',
                                    '@media $md': {
                                        display: 'block',
                                    },
                                }}
                                fontType="body"
                                marginX={0.5}
                            >
                                •
                            </Box>
                            <Paragraph>
                                <Anchor
                                    href={`${
                                        // This should always be non-null but
                                        // graphql currently says it can be.
                                        agent.profile?.profileUrl ?? ''
                                    }/#reviews`}
                                    color="brand"
                                    onClick={yieldCallback(() => {
                                        event({
                                            // https://dataportal.zillowgroup.net/event-registry/6095
                                            envelope: {
                                                event_type_id: '6095',
                                                event_type_version_id: '2',
                                                event_template_id: '171',
                                                event_template_version_id: '1',
                                                // Master ID: 6095.2.171.1
                                            },
                                            clickstream_trigger: {
                                                trigger_type_nm: 'interaction',
                                                trigger_location_nm: 'home',
                                                trigger_source_nm: 'my_agent_component',
                                                trigger_object_nm: 'my_agent_component|reviews',
                                            },
                                            semantic: {
                                                semantic_event_nm: 'my_agent_homepage_click_review',
                                            },
                                        });
                                    })}
                                >
                                    {agent.profile?.ratingCount}{' '}
                                    {agent.profile?.ratingCount === 1 ? 'Review' : 'Reviews'}
                                </Anchor>
                            </Paragraph>
                        </Box>
                    </Box>
                </Box>

                {/* Background photo */}
                <Box
                    as={Image}
                    src="https://www.zillowstatic.com/bedrock/app/uploads/sites/5/2024/06/YourTeam2.png"
                    alt=""
                    role="presentation"
                    sx={{
                        'overflow': 'hidden',
                        'objectFit': 'cover',
                        'overflow-clip-margin': 'unset',
                        'position': 'absolute',
                        'width': BACKGROUND_PHOTO_WIDTH_DESKTOP,
                        'height': 332,
                        'borderRadius': '$radii.lg',
                        'zIndex': -1,
                        'display': 'none',
                        'right': 0,
                        '@media $xl': {
                            display: 'block',
                        },
                    }}
                />
            </Box>
        </Box>
    );
};

const YourTeam: React.FC<{
    agent: BridgeTeamPAAgent;
    collectionProps: HomeRecsCarouselProps;
    userFirstName: string | null | undefined;
}> = ({ agent, collectionProps, userFirstName }) => {
    // This is needed given DAI don't return the agent name as part of the collection name
    const updatedCollectionProps = useMemo(() => {
        const updatedProps = { ...collectionProps };

        if (updatedProps.collection && agent.name) {
            const updatedCollection = { ...updatedProps.collection };
            updatedCollection.name = `Available to tour with your agent, ${agent.name}`;
            updatedProps.collection = updatedCollection;
        }

        return updatedProps;
    }, [collectionProps, agent.name]);

    useEffect(() => {
        event({
            // https://dataportal.zillowgroup.net/event-registry/6096
            envelope: {
                event_type_id: '6096',
                event_type_version_id: '1',
                event_template_id: '183',
                event_template_version_id: '1',
                // Master ID: 6096.1.183.1
            },
            clickstream_trigger: {
                trigger_type_nm: 'impression',
                trigger_location_nm: 'home',
                trigger_source_nm: 'my_agent_component',
                trigger_object_nm: 'no_trigger_object',
            },
            semantic: {
                semantic_event_nm: 'view_content',
            },
        });
    }, []);

    return (
        <Fragment>
            <RecsCarousel {...updatedCollectionProps} />
            <AgentContactRow agent={agent} userFirstName={userFirstName} />
        </Fragment>
    );
};

export default YourTeam;
